import '@styles/main.scss';

if (import.meta.env.DEV) {
  import('../styles/common/_global.scss');
}

/**
 * ---------------------------------------------------------
 * IMPORT SHARED CORE FROM PARENT
 * ---------------------------------------------------------
 */

import {
  hasElement,
  ensureFlickity,
  ensureModal,
  ensureCollapse,
  ensureSelect2,
} from '../../../../roots/resources/assets/scripts/main';

import initCommon from './common/common';

/**
 * ---------------------------------------------------------
 * COMPONENT REGISTRY
 * ---------------------------------------------------------
 */

const components = [
  {
    selector: '.carousel',
    delay: 3000,
    child: () =>
      import('./components/misc/flickityAdditions'),
    // parent: () =>
    //   import(
    //     '../../../../roots/resources/assets/scripts/components/misc/flickityAdditions'
    //   ),
  },
  {
    selector: '.roots.cookie-consent-wrp',
    dependencies: [ensureModal],
    delay: 10000,
    child: () =>
      import('./components/cookie'),
  },
  {
    selector: '.roots .editor-content-wrp',
    child: () =>
      import('./components/misc/editorContentAdditions'),
  },
  {
    selector: '.roots .gallery-wrapper, .product-detail-section .product-images-details-wrp',
    child: () =>
      import('./components/misc/jqueryViewer'),
  },
  {
    selector: '.roots.header',
    child: () =>
      import('./components/header'),
  },
  {
    selector: '.pre-footer-section',
    child: () =>
      import('./components/pre-footer'),
  },
  {
    selector: '.roots.footer',
    dependencies: [ensureCollapse],
    child: () =>
      import('./components/footer'),
  },
  {
    selector: '.roots.not-found-section',
    child: () =>
      import('./components/404'),
  },
  {
    selector: '.roots.news-listing-section',
    child: () =>
      import('./components/listing/'),
  },
  {
    selector: '.roots.home-banner-section',
    child: () =>
      import('./components/home/banner'),
  },
  {
    selector: '.roots.page-banner-section',
    child: () =>
      import('./components/banner'),
  },
  {
    selector: '.roots > .breadcrumb-wrp',
    child: () =>
      import('./components/breadcrumb'),
  },
  {
    selector: '.roots.one-column-section',
    child: () =>
      import('./components/fcp/one-column-section'),
  },
  {
    selector: '.roots.two-column-section',
    child: () =>
      import('./components/fcp/two-column-section'),
  },
  {
    selector: '.roots.three-column-section',
    child: () =>
      import('./components/fcp/three-column-section'),
  },
  {
    selector: '.roots.cards-section',
    dependencies: [ensureFlickity],
    child: () =>
      import('./components/fcp/cards-section'),
  },
  {
    selector: '.roots.gallery-section',
    dependencies: [ensureFlickity],
    child: () =>
      import('./components/fcp/gallery-section'),
  },
  {
    selector: '.roots.image-section',
    child: () =>
      import('./components/fcp/image-section'),
  },
  {
    selector: '.roots.accordion-section',
    child: () =>
      import('./components/fcp/accordion-section'),
  },
  {
    selector: '.roots.video-section',
    dependencies: [ensureModal],
    child: () =>
      import('./components/fcp/video-section'),
  },
  {
    selector: '.roots.cta-section',
    child: () =>
      import('./components/fcp/cta-section'),
  },
  {
    selector: '.roots.form-section',
    dependencies: [ensureSelect2],
    child: () =>
      import('./components/fcp/form-section'),
  },
  {
    selector: '.roots.logo-section',
    dependencies: [ensureFlickity],
    child: () =>
      import('./components/fcp/logo-section'),
  },
  {
    selector: '.fcp-testimonials-section',
    dependencies: [ensureFlickity],
    child: () =>
      import('./components/fcp/testimonials-section'),
  },
  {
    selector: '.home-scheduled-tour-section',
    child: () =>
      import('./components/home/scheduledTours'),
  },
  {
    selector: '.home-popular-tour-section',
    dependencies: [ensureFlickity],
    child: () =>
      import('./components/home/popularTours'),
  },
  {
    selector: '.home-testimonials-section:not(.fcp-testimonials-section)',
    dependencies: [ensureFlickity],
    child: () =>
      import('./components/home/testimonials'),
  },
  {
    selector: '.product-listing-section',
    child: () =>
      import('./components/woocommerce/listing'),
  },
  {
    selector: '.product-detail-section',
    child: () =>
      import('./components/woocommerce/detail'),
  },
  {
    selector: '.related-products-section',
    dependencies: [ensureFlickity],
    child: () =>
      import('./components/woocommerce/product'),
  },
  {
    selector: '.featured-tour-section',
    child: () =>
      import('./components/fcp/featured-tour'),
  },
  {
    selector: '.checkout-form-section',
    child: () =>
      import('./components/woocommerce/productCheckout'),
  },
  {
    selector: '.inquiry-form-section',
    child: () =>
      import('./components/woocommerce/inquireForm'),
  },
  {
    selector: '.js-tour-dates, .thank-you-section',
    child: () =>
      import('./components/woocommerce/bookingForm'),
  },
];

/**
 * ---------------------------------------------------------
 * HELPERS
 * ---------------------------------------------------------
 */

function sleep(ms) {
  return new Promise((resolve) =>
    setTimeout(resolve, ms)
  );
}

/**
 * ---------------------------------------------------------
 * COMPONENT LOADER
 * ---------------------------------------------------------
 */

async function loadComponent(component) {
  const {
    selector,
    child,
    // parent,
    dependencies = [],
    delay = 0,
  } = component;

  if (!hasElement(selector)) {
    return;
  }

  await Promise.all(
    dependencies.map((dependency) => dependency())
  );

  if (delay) {
    await sleep(delay);
  }

  let module = null;

  /**
   * TRY CHILD FIRST
   */

  try {
    module = await child();

    console.log(`[Child Loaded] ${selector}`);
  } catch (error) {
    console.error(
    '[Child Import Failed]',
    selector,
    error
  );

  return;
  }

  if (
    module?.default &&
    typeof module.default === 'function'
  ) {
    module.default();
  }
}

/**
 * ---------------------------------------------------------
 * INIT
 * ---------------------------------------------------------
 */

async function initTheme() {
  initCommon();

  await Promise.allSettled(
    components.map((component) =>
      loadComponent(component)
    )
  );
}

$(document).ready(function () {
  initTheme();
});
